import React, { useState, useEffect } from 'react';
import { addStaff, updateStaff } from '../../utils/supabaseClient';
import '../../styles/StaffForm.css';

const StaffForm = ({ onAddStaff, onClose, editStaff = null, isAdmin = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Initialize form data based on editStaff or with empty values
  const [formData, setFormData] = useState(() => {
    if (editStaff) {
      // If editing, map all fields from the staff object
      const initialData = {};
      
      // Map standard fields
      initialData.staff_no = editStaff.staff_no || '';
      initialData.name = editStaff.name || '';
      initialData.role = editStaff.role || 'Teacher';
      initialData.gender = editStaff.gender || '';
      initialData.qualification = editStaff.qualification || '';
      initialData.subject = editStaff.subject_specialization || '';
      initialData.phone = editStaff.mobile?.toString() || '';
      initialData.email = editStaff.email || '';
      initialData.aadhar = editStaff.aadhaar_no?.toString() || '';
      initialData.fathersAadhar = editStaff.fathers_aadhaar?.toString() || '';
      initialData.mothersAadhar = editStaff.mothers_aadhaar?.toString() || '';
      initialData.salary = editStaff.salary?.toString() || '';
      initialData.address = editStaff.address || '';
      initialData.dateOfJoining = editStaff.date_of_joining || '';
      initialData.dob = editStaff.dob || '';
      initialData.is_active = editStaff.is_active !== undefined ? editStaff.is_active : true;
      
      // Skip raw DB column names that are already mapped to form fields above
      // This prevents duplicate/conflicting data being sent during save
      
      return initialData;
    } else {
      // If adding new, use empty values
      return {
        staff_no: '',
        name: '',
        role: 'Teacher',
        gender: '',
        qualification: '',
        subject: '',
        phone: '',
        email: '',
        aadhar: '',
        fathersAadhar: '',
        mothersAadhar: '',
        salary: '',
        address: '',
        dateOfJoining: '',
        dob: '',
        is_active: true
      };
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!formData.name || !formData.phone) {
      setError('Please fill in all required fields (marked with *)');
      return;
    }
    
    // Validate phone number format if provided
    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone)) {
      setError('Please enter a valid 10-digit phone number (e.g., 9123456789)');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      let savedStaff;
      if (editStaff) {
        // Update existing staff
        // Always use staff_no as the primary key
        const staffId = editStaff.staff_no;
        console.log('Updating staff with staff_no:', staffId);
        console.log('Form data being sent:', formData);
        
        savedStaff = await updateStaff(staffId, formData);
        console.log('Staff updated successfully:', savedStaff);
      } else {
        // Add new staff
        console.log('Adding new staff with data:', formData);
        savedStaff = await addStaff(formData);
        console.log('Staff added successfully:', savedStaff);
      }
      
      if (!savedStaff) {
        throw new Error('No staff data returned from the server');
      }
      
      // Pass the saved staff data back to the parent component
      onAddStaff(savedStaff);
      onClose();
    } catch (err) {
      console.error('Error saving staff:', err);
      setError(`Failed to save staff: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="staff-form-container" onClick={e => e.stopPropagation()}>
        <div className="form-header">
          <h2>{editStaff ? 'Edit Staff' : 'Add New Staff'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Required"
            />
          </div>

          <div className="form-group">
            <label>Role *</label>
            <select
              name="role"
              value={formData.role || 'Teacher'}
              onChange={handleChange}
              required
            >
              <option value="Teacher">Teacher</option>
              <option value="Peon">Peon</option>
              <option value="Guard">Guard</option>
              <option value="Driver">Driver</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="dob"
                value={formData.dob || ''}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Qualification</label>
              <input
                type="text"
                name="qualification"
                value={formData.qualification || ''}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input
                type="text"
                name="subject"
                value={formData.subject || ''}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                pattern="[0-9]{10}"
                title="Please enter a valid 10-digit phone number"
                required
                placeholder="Required (10 digits)"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                name="aadhar"
                value={formData.aadhar || ''}
                onChange={handleChange}
                pattern="[0-9]{12}"
                title="Please enter a valid 12-digit Aadhar number"
                placeholder="Optional (12 digits)"
              />
            </div>
            <div className="form-group">
              <label>Date of Joining</label>
              <input
                type="date"
                name="dateOfJoining"
                value={formData.dateOfJoining || ''}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Father's Aadhaar</label>
              <input
                type="text"
                name="fathersAadhar"
                value={formData.fathersAadhar || ''}
                onChange={handleChange}
                pattern="[0-9]{12}"
                title="Please enter a valid 12-digit Aadhaar number"
                placeholder="Optional (12 digits)"
              />
            </div>
            <div className="form-group">
              <label>Mother's Aadhaar</label>
              <input
                type="text"
                name="mothersAadhar"
                value={formData.mothersAadhar || ''}
                onChange={handleChange}
                pattern="[0-9]{12}"
                title="Please enter a valid 12-digit Aadhaar number"
                placeholder="Optional (12 digits)"
              />
            </div>
          </div>

          {isAdmin && (
            <div className="form-row">
              <div className="form-group">
                <label>Salary</label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary || ''}
                  onChange={handleChange}
                  step="1"
                  min="0"
                  placeholder="Optional"
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Address</label>
            <textarea
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
              placeholder="Optional"
            ></textarea>
          </div>

          {error && <div className="error-message" style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editStaff ? 'Update Staff' : 'Add Staff')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffForm;
